import { SbBlokData, storyblokEditable } from "@storyblok/react"
import { AiFillStar } from "react-icons/ai"
import { Anchor } from "../../app/components/anchor"
import Image from "next/image"

import PrimaryButton from "../../components/buttons/PrimaryButton"
import ReviewCard, { IReview } from "../../components/cards/ReviewCard"
import useCTARoute from "../../util/useCTARoute"

import { useReviewsContext } from "../../context/ReviewsContext/ReviewsContextProvider"

export interface ICustomerReviews extends SbBlokData {
  heading: string
  buttonLabel: string
  reviews: Array<IReview & SbBlokData>
  buttonLink?: string
}

export default function CustomerReviews({
  blok,
  host,
}: {
  blok: ICustomerReviews
  host?: Record<string, string>
}) {
  const appHost = host?.app || process.env.NEXT_PUBLIC_APP_HOST || ""
  const {
    heading,
    buttonLabel,
    reviews,
    buttonLink = `${appHost}/shipments/new`,
  } = blok
  const mid = Math.ceil(reviews.length / 2)

  const { route } = useCTARoute()
  const { blokReviews } = useReviewsContext()
  return (
    <section
      {...storyblokEditable(blok)}
      className="page-padding flex flex-col"
    >
      <h3 className="mb-14 text-4xl text-blue7 text-left md:text-center font-montserrat font-medium">
        {heading}
      </h3>
      <section className="relative sm:max-h-[180vh] lg:max-h-screen grid grid-cols-1 md:grid-cols-2 gap-8 overflow-hidden">
        <div className="flex flex-col gap-8">
          {reviews.slice(0, mid).map((review, i) => (
            <ReviewCard review={review} key={i} />
          ))}
        </div>
        <div className="flex flex-col gap-8">
          {reviews.slice(mid).map((review, i) => (
            <ReviewCard review={review} key={i} />
          ))}
        </div>
        <div className="hidden md:block absolute bottom-0 left-0 w-full h-2/5 bg-gradient-to-b from-transparent to-white"></div>
      </section>

      <aside className="mt-10 w-full md:w-1/2 mx-auto grid grid-cols-2 gap-y-8 gap-x-4">
        <Anchor
          className="w-full col-span-2 mx-auto text-center text-white hover:text-white no-underline hover:no-underline"
          href={buttonLink || route}
        >
          <PrimaryButton>{buttonLabel}</PrimaryButton>
        </Anchor>
        <section className="px-4 md:px-8 py-4 rounded-2xl shadow-md">
          <Image
            className="mb-5 h-8 w-auto"
            src="/icons/google.svg"
            alt="google logo"
            width={100}
            height={20}
          />
          <p className="text-sm mb-2">Average Rating</p>
          <div className="w-full flex text-2xl items-center">
            {blokReviews?.googleRatings ?? "4.9"}
            {[...Array(5)].map((_, i) => (
              <AiFillStar
                key={i}
                className="h-5 w-5 fill-yellow12 stroke-yellow12 first-of-type:ml-2 last-of-type:mr-auto"
              />
            ))}
          </div>
        </section>
        <section className="px-4 md:px-8 py-4 rounded-2xl shadow-md">
          <Image
            className="mb-5 h-8 w-auto"
            src="/icons/trust-pilot.svg"
            alt="trust pilot logo"
            width={100}
            height={20}
          />
          <p className="text-sm mb-2">Average Rating</p>
          <div className="w-full flex text-2xl items-center">
            {blokReviews?.trustpilotRatings ?? "4.7"}
            {[...Array(5)].map((_, i) => (
              <AiFillStar
                key={i}
                className="h-5 w-5 fill-yellow12 stroke-yellow12 first-of-type:ml-2 last-of-type:mr-auto"
              />
            ))}
          </div>
        </section>
        <section className="col-span-2 px-4 md:px-8 py-4 rounded-2xl shadow-md flex flex-col items-center">
          <p className="mt-4 flex">
            {[...Array(5)].map((_, i) => (
              <AiFillStar
                key={i}
                className="h-5 w-5 fill-yellow12 stroke-yellow12 last-of-type:mr-auto"
              />
            ))}
          </p>
          <p>
            Transport service rating:{" "}
            <span className="font-semibold">
              {blokReviews?.transportServiceRatings ?? "4.7"}
            </span>{" "}
            -{" "}
            <Anchor href="/reviews" className="hover:underline">
              {blokReviews?.transportServiceReviews ?? 10231} reviews
            </Anchor>
          </p>
        </section>
      </aside>
    </section>
  )
}
